import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { defer, delay, Observable, of, throwError } from 'rxjs';
import { buildPageMeta, PageRequest } from '@/app/core/models/paged-result.model';
import {
  SettlementBonusPage,
  SettlementFilters,
  SettlementStatusOption,
  SettlementSummary,
} from '../models/settlement-query.model';
import { Settlement } from '../models/settlement.model';
import {
  MOCK_STATUS_OPTIONS,
  MockSettlementRecord,
  mockSettlementRecords,
} from './settlement.fixture';
import { SettlementQueryRepository } from './settlement-query.repository';

const MOCK_LATENCY_MS = 400;

const matches = ({ settlement }: MockSettlementRecord, filters: SettlementFilters): boolean => {
  const issueDate = settlement.issueDate.slice(0, 10);
  return (
    (filters.settlementId === undefined || settlement.settlementId === filters.settlementId) &&
    (filters.statusId === undefined || settlement.statusId === filters.statusId) &&
    (!filters.startDate || issueDate >= filters.startDate) &&
    (!filters.endDate || issueDate <= filters.endDate)
  );
};

const toSummary = ({ settlement }: MockSettlementRecord): SettlementSummary => ({
  settlementId: settlement.settlementId,
  statusId: settlement.statusId,
  status: settlement.status,
  opaNumber: settlement.opaNumber,
  opaPaymentDate: settlement.opaPaymentDate,
  linkDate: settlement.linkDate,
  totalAmount: settlement.totalAmount,
  issueDate: settlement.issueDate,
  providerRut: settlement.providerRut,
});

// El BFF solo expone el listado y los estados; el detalle y los bonos siguen pendientes (CA-787).
@Injectable()
export class SettlementQueryMockRepository extends SettlementQueryRepository {
  override search(filters: SettlementFilters): Observable<SettlementSummary[]> {
    return defer(() =>
      of(mockSettlementRecords.filter((record) => matches(record, filters)).map(toSummary)),
    ).pipe(delay(MOCK_LATENCY_MS));
  }

  override listStatusOptions(): Observable<SettlementStatusOption[]> {
    return of(MOCK_STATUS_OPTIONS.map((option) => ({ ...option }))).pipe(delay(MOCK_LATENCY_MS));
  }

  override getById(settlementId: number): Observable<Settlement> {
    return defer(() => {
      const record = this.find(settlementId);
      return record ? of({ ...record.settlement }) : throwError(() => notFound());
    }).pipe(delay(MOCK_LATENCY_MS));
  }

  override listBonuses(settlementId: number, page: PageRequest): Observable<SettlementBonusPage> {
    return defer(() => {
      const record = this.find(settlementId);
      if (!record) return throwError(() => notFound());
      const start = page.page * page.size;
      return of({
        content: record.bonuses.slice(start, start + page.size),
        page: buildPageMeta(page, record.bonuses.length),
      });
    }).pipe(delay(MOCK_LATENCY_MS));
  }

  private find(settlementId: number): MockSettlementRecord | undefined {
    return mockSettlementRecords.find((record) => record.settlement.settlementId === settlementId);
  }
}

const notFound = (): HttpErrorResponse => new HttpErrorResponse({ status: 404 });
